import { TelemetryEvent } from './TelemetryEvent';
import { TenantTokenManager } from './TenantTokenManager';
import { DataField } from './DataField';
import { TelemetryEventValidator } from './TelemetryEventValidator';
import { Event } from './Event';
import { logNotification, LogLevel, Category } from './OTelNotifications';
import { TelemetrySink } from './TelemetrySink';

declare var OSF: any;

export const SuppressNexus = -1;

export interface ISimpleTelemetryLogger {
  /**
   * onSendEvent: Events that fire whenever sendTelemetryEvent processes an event
   */
  onSendEvent: Event<TelemetryEvent>;

  /**
   * persistentDataFields: Data fields that will automatically be sent with every event
   */
  persistentDataFields: DataField[];

  /**
   * Send a telemetry event
   * @param event The event to send
   */
  sendTelemetryEvent(event: TelemetryEvent): void;

  /**
   * Add a sink for processing telemetry events
   * @param sink A telemetry sink to which events will be passed when sendTelemetryEvent is called.
   */
  addSink(sink: TelemetrySink): void;

  /**
   * Set tokens to use for sending telemetry to Aria and/or Nexus
   * The tokens will be used for events which whose namespace exactly matches
   * @param namespace The namespace for the tokens
   * @param ariaTenantToken Optional: The Aria tenant token
   * @param nexusTenantToken Optional: The Nexus tenant token
   */
  setTenantToken(namespace: string, ariaTenantToken?: string, nexusTenantToken?: number): void;

  /**
   * Set tokens to use for sending telemetry
   * The token tree is an object whose structure defines namespaces. At any level of the tree,
   * it can define values for 'ariaTenantToken' (string) and/or 'nexusTenantToken' (number).
   * These values are used as telemetry tokens for any events in that namespace or deeper.
   * @param tokenTree An object containing tokens to use when sending telemetry
   */
  setTenantTokens(tokenTree: object): void;
}

export class SimpleTelemetryLogger implements ISimpleTelemetryLogger {
  onSendEvent: Event<TelemetryEvent> = new Event<TelemetryEvent>();
  telemetryEnabled: boolean = true;
  queriedForTelemetryEnabled: boolean = false;
  persistentDataFields: DataField[] = [];

  constructor(parent?: ISimpleTelemetryLogger, persistentDataFields?: DataField[]) {
    if (parent) {
      this.onSendEvent = parent.onSendEvent;
      this.persistentDataFields.push(...parent.persistentDataFields);
    }
    if (persistentDataFields) {
      this.persistentDataFields.push(...persistentDataFields);
    }
  }

  public sendTelemetryEvent(event: TelemetryEvent) {
    try {
      if (!this.isTelemetryEnabled()) {
        return;
      }

      if (this.onSendEvent.getListenerCount() === 0) {
        logNotification(LogLevel.Warning, Category.Core, () => 'No telemetry sinks are attached.');
        return;
      }

      // Don't modify the event that got passed in; we create our own.
      let localEvent: TelemetryEvent = this.cloneEvent(event);
      this.processTelemetryEvent(localEvent);

      this.onSendEvent.fireEvent(localEvent);
    } catch (error) {
      let errorMessage: string;
      if (error instanceof Error) {
        errorMessage = error.message;
      } else {
        errorMessage = JSON.stringify(error);
      }
      logNotification(LogLevel.Error, Category.Core, () => errorMessage);
    }
  }

  public processTelemetryEvent(event: TelemetryEvent) {
    if (!event.telemetryProperties) {
      event.telemetryProperties = TenantTokenManager.getTenantTokens(event.eventName);
    }

    event.dataFields!.push(...this.persistentDataFields);

    TelemetryEventValidator.validateTelemetryEvent(event);
  }

  public addSink(sink: TelemetrySink) {
    this.onSendEvent.addListener(event => sink.sendTelemetryEvent(event as TelemetryEvent));
  }

  public setTenantToken(namespace: string, ariaTenantToken?: string, nexusTenantToken?: number) {
    TenantTokenManager.setTenantToken(namespace, ariaTenantToken, nexusTenantToken);
  }

  public setTenantTokens(tokenTree: object) {
    TenantTokenManager.setTenantTokens(tokenTree);
  }

  public setIsTelemetryEnabled_TestOnly(enabled: boolean) {
    this.telemetryEnabled = enabled;
    this.queriedForTelemetryEnabled = true;
  }

  /**
   * Clone a telemetry event; generally so we don't modify the input telemetry event
   * Also sets dataFields to [] if it isn't already set
   * @param event The event to clone
   */
  public cloneEvent(event: TelemetryEvent): TelemetryEvent {
    let localEvent: TelemetryEvent = { eventName: event.eventName, eventFlags: event.eventFlags };

    if (!!event.telemetryProperties) {
      localEvent.telemetryProperties = {
        ariaTenantToken: event.telemetryProperties.ariaTenantToken,
        nexusTenantToken: event.telemetryProperties.nexusTenantToken
      };
    }

    if (!!event.eventContract) {
      localEvent.eventContract = { name: event.eventContract.name, dataFields: event.eventContract.dataFields.slice() };
    }

    localEvent.dataFields = !!event.dataFields ? event.dataFields.slice() : [];
    return localEvent;
  }

  private isTelemetryEnabled(): boolean {
    if (!this.queriedForTelemetryEnabled) {
      this.telemetryEnabled = this.isTelemetryEnabledInternal();
      this.queriedForTelemetryEnabled = true;
    }

    return this.telemetryEnabled;
  }

  private isTelemetryEnabledInternal(): boolean {
    if (typeof OSF !== 'undefined') {
      // Check if we're in an Agave and telemetry is disabled
      if (
        typeof OSF.AppTelemetry === 'undefined' ||
        typeof OSF.AppTelemetry.enableTelemetry === 'undefined' ||
        OSF.AppTelemetry.enableTelemetry === false
      ) {
        logNotification(LogLevel.Warning, Category.Core, () => 'AppTelemetry is disabled for this platform.');
        return false;
      }
    }
    return true;
  }
}
